import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, Grid, Link, Stack, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import more from './more.svg';
import { setPath } from 'src/store/Navigation';
import CircularProgress from '@mui/material/CircularProgress';
import { IStartUpMenu, IStartUpProgress, ISubMenu } from 'src/types/SummaryDashboard';
import { usePermissionWrapper } from 'src/components/hooks/usePermissionWrapper';
import { setMessage } from 'src/store/Message';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { useTab } from 'src/components/hooks/useTab';
import { useLazyGetStartupDashboardDataQuery } from 'src/services/SummaryDashboard';
import { useSelectedAppInfo } from 'src/util/SelectedAppInfo';
import { useState, useEffect } from 'react';
import { archLevel } from 'src/data/config';

const StartupProgressWidget: React.FunctionComponent<any> = (props: { startupProgressData: IStartUpProgress }) => {
  const { startupProgressData } = props;
  const [startupProgressObj, setStartupProgressObj] = useState(startupProgressData);
  const [isWidgetDataLoading, setIsWidgetDataLoading] = useState(false);
  const { setTabVal, tabsData } = useTab();
  const { checkPermission } = usePermissionWrapper();
  const dispatch = useDispatch();
  const dispatchApp = useAppDispatch();
  const { selectedApp } = useSelectedAppInfo();
  const studyId = useAppSelector(state => state.site.selectedStudy);
  const selectedCountry = useAppSelector(state => state.site.selectedCountry);
  const selectedSite = useAppSelector(state => state.site.selectedSite);
  const appName = archLevel == 'site' ? process.env.REACT_APP_NAME_TEMP : process.env.REACT_APP_NAME;

  const [getStartupDashboardData, { error: startupDashboardError, isError: startupDashboardIsError }] =
    useLazyGetStartupDashboardDataQuery();

  const getStartupWidgetData = async () => {
    setIsWidgetDataLoading(true);
    const StartupResponse: any = await getStartupDashboardData({
      site_id: selectedSite,
      study_id: studyId,
      country: selectedCountry,
      application: appName,
      application_section: selectedApp
    });
    if (StartupResponse.isSuccess) {
      setStartupProgressObj(StartupResponse.data);
    } else if (StartupResponse.isError) {
      setStartupProgressObj({} as IStartUpProgress);
    }
    setIsWidgetDataLoading(false);
  };

  useEffect(() => {
    setStartupProgressObj(startupProgressData);
    getStartupWidgetData();
  }, [selectedSite]);

  useEffect(() => {
    if (startupDashboardIsError) {
      setStartupProgressObj({} as IStartUpProgress);
      const err: any = startupDashboardError;
      if (err?.status == 500) {
        dispatchApp(
          setMessage({
            msgDetails: `${err?.status} : Internal Server Error`,
            severity: 'error'
          })
        );
      } else if (err?.status == 403) {
        dispatchApp(
          setMessage({
            msgDetails: 'Error 403 Restricted User permission',
            severity: 'error'
          })
        );
      }
    }
  }, [startupDashboardIsError]);
  const getStatusProgress = (statusObj: IStartUpMenu) => {
    if (statusObj.percentage === 100) {
      return (
        <CheckCircleIcon color="success" style={{ marginRight: '8px', verticalAlign: 'middle', color: '#4CAF50' }} />
      );
    } else {
      return (
        <div style={{ position: 'relative', marginRight: '10px' }}>
          <CircularProgress
            variant="determinate"
            value={100}
            size={20}
            thickness={7}
            style={{ color: '#D1D1D1', position: 'absolute' }}
          />
          <CircularProgress
            variant="determinate"
            style={{ color: '#2196F3' }}
            value={statusObj.percentage}
            thickness={7}
            size={20}
          />
        </div>
      );
    }
  };
  const showNoAccessMessage = (name: string) => {
    dispatchApp(
      setMessage({
        msgDetails: `No Access Available for ${name}`,
        severity: 'error'
      })
    );
  };
  const resetTabIndexValue = (indexKey: string) => {
    const currentTabinfo: any = tabsData.filter((item: any) => item.key === indexKey);
    setTabVal(currentTabinfo[0].index);
  };
  const startupWidgetRedirection = (menuOrSection: string, menuName: string, sectionName: string) => {
    //Redirection should be taken care with Permission.
    switch (menuName) {
      case 'Site Contact Info':
        if (checkPermission('view_site_contact_info')) {
          if (menuOrSection === 'section') {
            //path to identify from menu - to menu - section
            const pathValue = `Summary-ContactInfo-${sectionName === 'Add Site Personnel' ? 'personnel' : 'siteinfo'}`;
            dispatch(setPath(pathValue));
          }
          resetTabIndexValue('siteContactInfo');
        } else {
          showNoAccessMessage(menuOrSection === 'menu' ? menuName : sectionName);
        }
        break;
      case 'Start Up Packet':
        if (checkPermission('view_startUp_packet')) {
          if (menuOrSection === 'section') {
            //path to identify from menu - to menu - section
            const pathValue = `Summary-StartUpPacket-${
              sectionName === 'Study Information' ? 'studyinfo' : 'contract-budget'
            }`;
            dispatch(setPath(pathValue));
          }
          resetTabIndexValue('startUpPacket');
        } else {
          showNoAccessMessage(menuOrSection === 'menu' ? menuName : sectionName);
        }
        break;
      case 'SQ Checklist':
        if (checkPermission('view_sq_checklist_pil')) {
          if (menuOrSection === 'section') {
            //path to identify from menu - to menu - section
            const pathValue = `Summary-sqChecklist-${getPathNameForStartupPacket(sectionName)}`;
            dispatch(setPath(pathValue));
          }
          resetTabIndexValue('sqChecklist');
        } else {
          showNoAccessMessage(menuOrSection === 'menu' ? menuName : sectionName);
        }
        break;
      case 'Study Logs':
        if (checkPermission('view_study_logs')) {
          if (
            menuOrSection === 'section' &&
            ((sectionName === '1572' && checkPermission('view_1572_form')) ||
              (sectionName === 'DOA' && checkPermission('view_site_doa')) ||
              (sectionName === 'SIV Training Log' && checkPermission('view_site_siv_training')))
          ) {
            //path to identify from menu - to menu - section
            const pathValue = `Summary-StudyLogs-${
              sectionName === '1572' || sectionName === 'DOA' ? sectionName : 'TrainingLog'
            }`;
            dispatch(setPath(pathValue));
            resetTabIndexValue('studyLogs');
          } else if (menuOrSection === 'menu') {
            resetTabIndexValue('studyLogs');
          } else {
            showNoAccessMessage(sectionName);
          }
        } else {
          showNoAccessMessage(menuOrSection === 'menu' ? menuName : sectionName);
        }
        break;
      default:
    }
  };
  const getStatuProgressIcon = (statusObj: ISubMenu) => {
    if (statusObj.completeness === 'completed') {
      return <CheckCircleIcon style={{ marginRight: '8px', verticalAlign: 'middle', color: '#4CAF50' }} />;
    } else {
      return <img src={more} className="InprogressStat" alt={statusObj.completeness} />;
    }
  };
  const getPathNameForStartupPacket = (sectionName: string) => {
    return sectionName === 'Prior to the Visit'
      ? 'priorvisit'
      : sectionName === 'Supporting Documentation'
      ? 'supportdoc'
      : sectionName === 'During Site Qualification Visit'
      ? 'dsqvisit'
      : sectionName === 'After Site Qualification Visit'
      ? 'asqvisit'
      : '';
  };
  return (
    <Grid
      item
      xs={4.5}
      style={{ border: '1px solid #efefef', paddingLeft: '8px', paddingTop: '16px', borderRadius: '4px' }}
    >
      {startupProgressObj && (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ position: 'relative', marginRight: '10px' }}>
            <CircularProgress
              variant="determinate"
              value={100}
              size={60}
              thickness={7}
              style={{ color: '#D1D1D1', position: 'absolute' }}
            />
            <CircularProgress
              variant="determinate"
              style={{ color: '#4CAF50' }}
              value={startupProgressObj?.percentage}
              thickness={7}
              size={60}
            />
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
                width: '100%',
                marginTop: '-2px'
              }}
            >
              <Typography variant="body1" color="textSecondary">
                {`${startupProgressObj?.percentage}%`}
              </Typography>
            </div>
          </div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{startupProgressObj?.name}</h2>
          {isWidgetDataLoading && <CircularProgress size="1.5rem" style={{ position: 'relative', left: '5px' }} />}
        </div>
      )}
      {startupProgressObj?.menus?.length &&
        startupProgressObj?.menus?.map((progressObj: IStartUpMenu, index: number) => (
          <Stack key={`${progressObj.name}_${index}`} spacing={2}>
            <Box className="ProgressStats">
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {getStatusProgress(progressObj)}
                <Typography variant="body1" style={{ verticalAlign: 'middle' }}>
                  <Link onClick={() => startupWidgetRedirection('menu', progressObj.name, '')} className="link-btn ">
                    {progressObj.name}
                  </Link>
                  {`(${progressObj.percentage}% Complete)`}
                </Typography>
              </div>
              {progressObj.sub_menus?.length && (
                <ul>
                  {progressObj.sub_menus?.map((menu: ISubMenu, subIndex: number) => (
                    <li key={`${menu.name}_${subIndex}`}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {getStatuProgressIcon(menu)}
                        <Typography variant="body1" style={{ verticalAlign: 'middle' }}>
                          <Link
                            onClick={() => startupWidgetRedirection('section', progressObj.name, menu.name)}
                            className="link-btn "
                          >
                            {menu.name}
                          </Link>
                        </Typography>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Box>
          </Stack>
        ))}
    </Grid>
  );
};

export default StartupProgressWidget;
